import { ERROR_FIELD, JSON_PG, parseNameValue } from '@query/shared';
import type { ColDef, ValueGetterParams } from 'ag-grid-community';
import { dateComparator, filterParamsComparator } from './dates.js';

export type GridRow = Record<string, unknown>;

export interface ColumnsContext {
  /** Поле columns текущего запроса — если заполнено, оно и задаёт колонки. */
  columns: string;
  /** Загрузка значений для редактора-списка из представления v_*. */
  loadLookupValues: (view: string) => Promise<string[]>;
  /** Класс ячейки: состояние строки (upd/del/ins/copy) плюс класс из описания колонки. */
  cellStateClass: (nodeId: string | undefined, columnCellClass: string | undefined) => string;
}

/** Сообщение о проблемной колонке — чтобы не ронять всю таблицу из-за одной записи. */
export interface ColumnsResult {
  columnDefs: ColDef<GridRow>[];
  warnings: string[];
}

/**
 * Построение описания колонок — перенос arrayOfColumnDefs (AppFCFun.kt:163-209).
 *
 * Источник колонок выбирается так же, как в оригинале:
 *   1) ошибка загрузки → одна колонка error;
 *   2) заполненное поле columns текущего запроса → разбор DSL;
 *   3) иначе — вывод из данных первой строки.
 */
export async function buildColumnDefs(
  rows: GridRow[],
  ctx: ColumnsContext,
): Promise<ColumnsResult> {
  const warnings: string[] = [];
  const first = rows[0];

  let source: string[];
  if (first !== undefined && JSON.stringify(first).includes(ERROR_FIELD)) {
    source = ['field: error'];
  } else if (ctx.columns !== '') {
    source = ctx.columns.split(';');
  } else {
    source = first === undefined ? [] : inferColumns(first);
  }

  const defs: ColDef<GridRow>[] = [];
  const nonEmpty = source.filter((item) => item.trim() !== '');

  for (const [index, columnStr] of nonEmpty.entries()) {
    try {
      defs.push(await buildOneColumn(columnStr, index === 0, ctx));
    } catch (error) {
      // Оригинал падал целиком: parseNameValue бросал исключение на строке без «:».
      // Здесь пропускаем только проблемную колонку, остальные остаются рабочими.
      warnings.push(`колонка "${columnStr.trim()}": ${(error as Error).message}`);
    }
  }

  // Первая колонка сортируется по возрастанию — как в оригинале (`sort: asc`).
  if (defs[0] !== undefined) defs[0].sort = 'asc';

  return { columnDefs: defs, warnings };
}

async function buildOneColumn(
  columnStr: string,
  isFirst: boolean,
  ctx: ColumnsContext,
): Promise<ColDef<GridRow>> {
  // Каждая колонка описывается парами «ключ: значение» через запятую.
  const columnMap = Object.fromEntries(
    columnStr
      .split(',')
      .map((pair) => pair.trim())
      .filter((pair) => pair !== '')
      .map((pair) => {
        const parsed = parseNameValue(pair);
        return [parsed.name, parsed.value];
      }),
  );

  const field = columnMap['field'] ?? '';
  const cellEditor = columnMap['cellEditor'];
  const cellEditorParams = await buildEditorParams(cellEditor, columnMap['cellEditorParams'] ?? '', ctx);

  const def: ColDef<GridRow> = {
    field,
    headerName: columnMap['headerName'] ?? field,
    filter: columnMap['filter'] ?? 'agTextColumnFilter',
    sortable: (columnMap['sortable'] ?? 'true') === 'true',
    editable: (columnMap['editable'] ?? 'true') === 'true',
    resizable: (columnMap['resizable'] ?? 'true') === 'true',
    headerCheckboxSelection: isFirst,
    headerCheckboxSelectionFilteredOnly: true,
    filterParams: { comparator: filterParamsComparator },
    cellClass: (params: { node?: { id?: string | undefined } }) =>
      ctx.cellStateClass(params.node?.id, columnMap['cellClass']),
    valueGetter: (params: ValueGetterParams<GridRow>) => params.data?.[field],
  };

  if (cellEditor !== undefined) {
    def.cellEditor = cellEditor;
    def.cellEditorPopup = cellEditor === 'agLargeTextCellEditor';
    if (cellEditorParams !== null) def.cellEditorParams = cellEditorParams;
  }

  // Колонки с датами получают компаратор, иначе сортировка была бы строковой.
  if (field.includes('date')) {
    def.comparator = dateComparator;
  }

  assignNumber(def, 'width', columnMap['width']);
  assignNumber(def, 'flex', columnMap['flex']);
  assignNumber(def, 'minWidth', columnMap['minWidth']);

  assignIfPresent(def, 'type', columnMap['type']);
  assignIfPresent(def, 'headerClass', columnMap['headerClass']);
  assignIfPresent(def, 'pinned', columnMap['pinned']);
  assignIfPresent(def, 'headerTooltip', columnMap['headerTooltip']);
  assignIfPresent(def, 'colSpan', columnMap['colSpan']);

  if (columnMap['rowDrag'] !== undefined) def.rowDrag = columnMap['rowDrag'] === 'true';
  if (columnMap['checkboxSelection'] !== undefined) {
    def.checkboxSelection = columnMap['checkboxSelection'] === 'true';
  }
  if (columnMap['wrapText'] !== undefined) def.wrapText = columnMap['wrapText'] === 'true';
  if (columnMap['autoHeight'] !== undefined) def.autoHeight = columnMap['autoHeight'] === 'true';

  return def;
}

/**
 * Параметры редактора — перенос setEditor (AppFCFun.kt:325-343).
 * Значения для списка берутся из представления, если имя параметра содержит «v_».
 */
async function buildEditorParams(
  editorType: string | undefined,
  editorParam: string,
  ctx: ColumnsContext,
): Promise<Record<string, unknown> | null> {
  if (editorType === undefined || editorType === '') return null;

  const values = editorParam.includes('v_')
    ? await ctx.loadLookupValues(editorParam)
    : editorParam.split('.');

  if (editorType === 'agLargeTextCellEditor') return { maxLength: '1000', rows: '10', cols: '60' };
  if (editorType === 'agSelectCellEditor') return { values };
  return null;
}

/** Вывод колонок из данных — перенос getColumns (AppFCFun.kt:422-439). */
export function inferColumns(row: GridRow): string[] {
  return JSON.stringify(row)
    .split(',')
    .map((fragment) => {
      const field = (fragment.trim().replace(/[\n{}"']/g, '').split(':')[0] ?? '').trim();
      const tail = fragment.trim().replace(/[\n }]/g, '');
      const lastChar = tail.slice(-1);

      // Значение пришло без кавычек — значит это число.
      if (tail !== '' && lastChar !== '"') {
        return `field:${field},type: numericColumn, width: 90, headerClass: ag-right-aligned-header, cellClass: ag-right-aligned-cell, filter: agNumberColumnFilter `;
      }
      if (field.includes('date')) {
        return `field:${field}, width: 140, filter: agDateColumnFilter `;
      }
      return `field:${field}`;
    });
}

/** Формирование адреса представления-справочника для редактора списка. */
export function lookupUrl(urlInput: string, view: string): string {
  const prefix = urlInput.split(JSON_PG)[0] ?? '';
  return `${prefix}${JSON_PG}/${view}`;
}

function assignNumber(def: ColDef<GridRow>, key: 'width' | 'flex' | 'minWidth', raw?: string): void {
  if (raw === undefined || raw.trim() === '') return;
  const value = Number(raw);
  if (Number.isFinite(value)) def[key] = value;
}

function assignIfPresent(
  def: ColDef<GridRow>,
  key: 'type' | 'headerClass' | 'pinned' | 'headerTooltip' | 'colSpan',
  raw?: string,
): void {
  if (raw === undefined || raw.trim() === '') return;
  // Значения приходят строкой из DSL; ag-grid ожидает соответствующий тип.
  (def as Record<string, unknown>)[key] = key === 'colSpan' ? Number(raw) : raw;
}

/** Значения для редактора-списка из строк представления (поле param). */
export function pickLookupValues(rows: Array<Record<string, unknown>>): string[] {
  return rows.map((row) => String(row['param'] ?? ''));
}
